import { Component, ElementRef, ViewChild } from "@angular/core";
import { ActivityService } from "../../services/activity.service";
import { AuthenticationService, EnvironmentConfig } from "../../../core/core";
import { ToastrService } from "ngx-toastr";
import { ActivityTypeBaseComponent } from "../activity-type-base/activity-type-base.component";
import { RentalRequest, RentalRequestItem, RentalRequestService } from "../../../order/order";
import SignaturePad from "signature_pad";
import { generate, observe } from "fast-json-patch";
import { forkJoin, of } from "rxjs";
import { Cart, CartItem, CartService } from "../../../cart/cart";
import { AzureSearchService } from "../../../azure-search/azure-search";
import { Account, AccountService } from "../../../account/account";
import { TransportationService } from "../../../transportation/transportation";
import { InventoryGroup, InventoryItem } from "../../../inventory/inventory";

@Component({
  selector: "common-activity-machine-availability",
  templateUrl: "./activity-machine-availability.component.html",
  styleUrls: ["activity-machine-availability.component.scss"],
})
export class ActivityMachineAvailabilityComponent extends ActivityTypeBaseComponent {
  baseImagePath: string;

  available: boolean;
  canProvideTransport: boolean = false;
  signatureValid: boolean = false;

  terms = [
    {
      label:
        "I agree to the stated earnings calculations and that these earnings are inclusive of all charges and fees related, but not limited to, maintenance, heavy equipment, environmental, and fuel taxes.",
      checked: false,
      tooltip: "If you wish to create an invoice for your earnings, the invoice amount shall not exceed the stated earnings for the applicable rental term.",
    },
    {
      label: "I agree that I will not solicit the rental customer in an attempt to circumvent the Rubbl rental program.",
      checked: false,
    },
    {
      label:
        "I agree that Rubbl will deduct mechanical breakdown repair costs and equipment downtime, unrelated to renter misuse or excessive wear, from my rental earnings. Rubbl will notify you in advance for estimate and approvals.",
      checked: false,
    },
    {
      label:
        "In order to insure and protect my equipment, I agree to perform all mandatory inspections. I agree that Rubbl will deduct $200 for each unsubmitted inspection in addition to any related field service charges.",
      checked: false,
    },
    {
      label: "I agree and have read the <a class='ml-1' target='_blank' href='https://support.rubbl.com/gps-return-policy'>GPS return policy</a>.",
      checked: false,
    },
    {
      label:
        "<div>I agree to the Rubbl <a class='ml-1' target='_blank' href='https://company.rubbl.com/pubweb/terms/owner-terms.pdf'>Terms of Service</a> and the Rubbl<a class='ml-1' target='_blank' href='https://support.rubbl.com/repair-policy'>Repair Policy</a>.</div>",
      checked: false,
    },
  ];
  rentalRequestItem: RentalRequestItem;
  rentalRequest: RentalRequest;

  @ViewChild("canvas") canvas: ElementRef;
  signaturePad: SignaturePad;
  accountPercentage; // pull from account
  rentalRate: number;
  netEarnings: number;
  totalNetEarnings: number;
  cart: Cart;
  loaded: boolean = false;
  cartItem: CartItem;
  ownerAccount: Account;
  rentalTerm: string;
  machine: InventoryItem;
  milesAway: number;
  notAvailableReason: string;
  attachmentRentalRate: number;
  transpoQuote?: number;

  constructor(
    activityService: ActivityService,
    authService: AuthenticationService,
    toastr: ToastrService,
    private cartService: CartService,
    private azureSearchService: AzureSearchService,
    private accountService: AccountService,
    private rentalRequestService: RentalRequestService,
    private transportationService: TransportationService,
  ) {
    super(activityService, authService, toastr);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.baseImagePath = this.environment.baseImagePath;
    forkJoin({
      rentalRequest: this.rentalRequestService.get(this.activity.rentalRequestId),
      machine: this.azureSearchService.getOne(this.activity.inventoryItemId, "machines"),
    }).subscribe(results => {
      this.rentalRequest = results.rentalRequest;
      this.rentalRequestItem = results.rentalRequest.items.find(d => d.inventoryItemSummary.id == this.activity.inventoryItemId);
      this.machine = results.machine as InventoryItem;
      // omg a subscribe within a subscribe, don't give a crap right now

      const inventoryGroup$ = this.machine.inventoryGroupId
        ? this.azureSearchService.getOne<InventoryGroup>(this.machine.inventoryGroupId, "inventorygroups")
        : of(null);

      forkJoin({
        cart: this.cartService.get(results.rentalRequest.cartId),
        ownerAccount: this.accountService.get(this.machine.accountId),
        inventoryGroup: inventoryGroup$,
      }).subscribe(result => {
        this.cart = result.cart;
        this.ownerAccount = result.ownerAccount;
        this.cartItem = result.cart.items.find(d => d.inventoryItem.id == this.activity.inventoryItemId);

        this.rentalTerm = this.cartService.isWeeklyOrMonthly(this.cartItem.duration);
        let chargeSummary = this.rentalTerm == "week" ? this.cartItem.weeklySummary : this.cartItem.monthlySummary;
        if (this.rentalTerm == "day") {
          chargeSummary = this.cartItem.dailySummary;
        }

        this.rentalRate = chargeSummary.Rent;
        this.attachmentRentalRate = chargeSummary?.Attachment || 0;
        let accountPercentage: number = this.ownerAccount.configuration?.billing?.configuration?.ownerPercentage || 0.7;
        if (this.activity.status === "Closed" && this.activity.metadata?.ownerPercentage) {
          accountPercentage = +this.activity.metadata?.ownerPercentage;
        }

        this.accountPercentage = accountPercentage;

        if (!result.inventoryGroup) {
          // fix availability check
          this.netEarnings = (this.rentalRate + this.attachmentRentalRate) * accountPercentage;
          switch (this.rentalTerm) {
            case "day":
              const dailyAttachment = this.cartItem.dailySummary?.Attachment || 0;
              this.totalNetEarnings = (this.cartItem.dailySummary.Rent + dailyAttachment) * accountPercentage;

              break;
            case "week":
              // for some reason the charge summary for weekly does not do the total
              const numberOfWeeks = this.cartItem.duration / 7;
              const weeklyAttachment = this.cartItem.weeklySummary?.Attachment || 0;
              this.totalNetEarnings = numberOfWeeks * (this.cartItem.weeklySummary.Rent + weeklyAttachment) * accountPercentage;
              break;
            case "month":
              const totalAttachment = this.cartItem.chargeSummary?.Attachment || 0;
              this.totalNetEarnings = (this.cartItem.chargeSummary.Rent + totalAttachment) * accountPercentage;
              break;
            default:
              this.totalNetEarnings = this.netEarnings;
              break;
          }
        } else {
          // since its an inventory group we just need to supply the owner commission of the inventory group
          // for billing transactions the backend is taking care of this as well
          this.netEarnings = this.rentalTerm == "week" ? result.inventoryGroup.ownerCommissionWeekly : result.inventoryGroup.ownerCommission;
          if (this.rentalTerm == "day") {
            this.netEarnings = this.rentalRate * this.accountPercentage;
          }
        }

        // no matter what always override net earnings if its closed
        if (this.activity.status === "Closed" && this.activity.metadata?.netEarnings) {
          this.netEarnings = +this.activity.metadata?.netEarnings;
        }

        const jobsite = this.rentalRequest.transportationInfo.deliveryAddress;

        this.milesAway = this.transportationService.calculateDistance(
          this.machine.location.longitude,
          this.machine.location.latitude,
          jobsite.longitude,
          jobsite.latitude,
        );

        this.transpoQuote = this.milesAway * 2;

        this.loaded = true;
      });
    });
  }

  machineAvailableChange(available) {
    this.available = available;
    if (available) {
      setTimeout(() => {
        const width = this.canvas.nativeElement.parentNode.clientWidth;
        this.canvas.nativeElement.setAttribute("width", width);
        this.signaturePad = new SignaturePad(this.canvas.nativeElement, {
          backgroundColor: "rgb(255, 255, 255)",
        });

        this.signaturePad.addEventListener(
          "endStroke",
          () => {
            this.signatureValid = true;
          },
          { once: true },
        );
      }, 400);
    } else {
      this.signatureValid = false;
    }
  }

  notAvailableReasonSelected(reason: string) {
    this.notAvailableReason = reason;
  }

  save() {
    const watcher = observe(this.activity);
    if (this.available) {
      this.activity.metadata.terms = JSON.stringify(this.terms);
      this.activity.metadata.canProvideTransport = this.canProvideTransport.toString();
      if (!this.canProvideTransport) {
        this.transpoQuote = null;
      }

      this.activity.metadata.transportQuote = this.transpoQuote ? this.transpoQuote.toString() : null;
      this.activity.metadata.ownerPercentage = this.accountPercentage?.toString();
      this.activity.metadata.signatureDataUrl = this.signaturePad.toDataURL("image/jpeg", 0.2);
      this.activity.metadata.netEarnings = this.netEarnings.toString();
    } else {
      this.activity.metadata.notAvailableReason = this.notAvailableReason;
    }

    this.activity.activityResponse = this.available.toString();
    this.activity.status = "Closed";
    this.activity.completedDateTime = new Date().toJSON();
    this.activity.completedByUserId = this.user.profile.sub;

    const patch = generate(watcher);
    this.activityService.jsonPatchWithPartitionKey(this.activity.id, patch, this.activity.accountId).subscribe(result => {
      this.toastr.success("Submitted");
      this.activity.metadata.terms = JSON.parse(this.activity.metadata.terms);
      window.scrollTo(0, 0);
    });
  }

  get disableButton() {
    if (this.environment.clientId == "rubbl.admin" && window.location.host != "localhost:4200") {
      return true;
    }

    if (this.available == null) {
      return true;
    }

    if (this.available == false) {
      if (!this.notAvailableReason) {
        return true;
      }
      return false;
    }

    if (!this.signatureValid) {
      return true;
    }

    if (!this.terms.every(d => d.checked)) {
      return true;
    }

    return false;
  }
}
